// QR generation for the activation pack, ported from HG-CC's
// `scripts/collateral/build-activation-pack.mjs` so a pack built here and a
// pack built from the terminal carry codes at the same scannability floor.
//
// `qrcode` rather than `qrcode.react`: the floor below is a check on printed
// module size, and that needs the module count, which the React component does
// not expose. The SVG string is injected straight into the sheet, so every code
// in a saved PDF stays vector.

import QRCode from "qrcode";
import { escapeHtml, markUrl } from "./tokens";
import type { PrintBrand } from "./printBrand";

/**
 * Branded, per Tim's 2026-08-06 activation feedback: modules print in a deep
 * brand ink rather than pure black, and codes 30mm and larger carry a mark on a
 * white plaque in the centre.
 *
 * REVISED 2026-08-09 — the ink and the centre mark are the CUSTOMER's, not Help
 * Genie's. `brand.inkDeep` is derived past a 10:1 contrast floor against white
 * precisely so it can be used here: the whole code, finder patterns included,
 * prints in one colour and a phone camera locks the corners in poor light.
 *
 * One thing is still deliberately unbranded, for scanning reasons. The quiet
 * zone stays white. Brand colour running to the code edge is the single most
 * common way a "designed" QR stops scanning, and no amount of it being the
 * customer's own colour makes that safe.
 */
const QR_DARK = "#1A2C4F";
const MARK_MIN_MM = 30;
const MARK_FRACTION = 0.2;

export interface PackQr {
  /** SVG markup, width/height stripped so CSS sizes it in millimetres. */
  svg: string;
  /** Modules across one edge, including the quiet zone. */
  modules: number;
  ecc: "H" | "Q" | "M";
  /** Printed size of one module at the requested piece size. */
  moduleMm: number;
}

/**
 * Minimum printed module size. 0.55mm modules failed to decode from a 300dpi
 * raster on the round sticker: the module grid does not land cleanly on the
 * pixel grid at that density. 0.65mm clears it with room to spare.
 */
export const MIN_MODULE_MM = 0.65;

/**
 * Thrown when a piece is too small to carry this genie's URL at a scannable
 * module size. Its own class, not a bare Error, because the document builders
 * catch exactly this and drop the piece — and must not swallow a real layout
 * bug while doing it.
 */
export class QrFloorError extends Error {
  constructor(
    message: string,
    readonly piece: string,
    readonly sizeMm: number,
  ) {
    super(message);
    this.name = "QrFloorError";
  }
}

/**
 * Runs a page builder and returns null if the piece cannot carry a scannable
 * code, rather than failing the whole document. A customer whose genie slug is
 * 70 characters long still gets every sheet that does work.
 */
export async function printable<T>(build: () => Promise<T>): Promise<T | null> {
  try {
    return await build();
  } catch (error) {
    if (error instanceof QrFloorError) return null;
    throw error;
  }
}

async function qrSvg(url: string, ecc: "H" | "Q" | "M", ink: string = QR_DARK) {
  const svg = await QRCode.toString(url, {
    type: "svg",
    errorCorrectionLevel: ecc,
    margin: 1, // quiet zone, in modules
    color: { dark: ink, light: "#FFFFFF" },
  });
  const vb = svg.match(/viewBox="0 0 (\d+)/);
  const modules = vb ? Number(vb[1]) : 0;
  const clean = svg
    .replace(/<\?xml[^>]*\?>/, "")
    .replace(/\swidth="[^"]*"/, "")
    .replace(/\sheight="[^"]*"/, "");
  return { svg: clean, modules };
}

/**
 * The smallest square this URL can be printed in and still clear the module
 * floor. The sticker book prints it, so a reader can see why there is no 38mm
 * round in the book rather than assuming one was forgotten.
 */
export async function minQrSizeMm(url: string): Promise<number> {
  const q = await qrSvg(url, "M");
  return q.modules * MIN_MODULE_MM;
}

/**
 * The largest error correction level that still clears the module floor at
 * `sizeMm`. Long genie URLs cannot hold level H on a small sticker, so this
 * steps down rather than shipping a code nobody can scan — and throws outright
 * when even the lowest level cannot make the size work.
 */
/**
 * The brand mark for the middle of a code, as markup to drop in beside the SVG.
 * Returns empty when it would not be safe:
 *
 * Level H tolerates roughly 30% occlusion and the plaque covers about 10% of
 * the code area, so it only ever goes on at level H. A code that fell back to
 * Q or M to keep its modules above the print floor has no damage budget left to
 * spend on decoration, and a sticker on a van needs that budget for scuffs.
 *
 * The plaque snaps to a whole number of modules so its edges land on the module
 * grid rather than slicing a module in half, which is what turns a "designed"
 * QR into an undecodable one.
 */
export function qrCentreMark(q: PackQr, sizeMm: number, brand?: PrintBrand): string {
  if (sizeMm < MARK_MIN_MM || q.ecc !== "H") return "";
  const modules = Math.round((q.modules * MARK_FRACTION) / 2) * 2 + 1;
  const mm = modules * q.moduleMm;
  const inner = mm * 0.68;
  // The customer's own logo in the middle of their own code. `contain` rather
  // than a fixed square: a wide wordmark stretched to a square is worse than a
  // small one, and the plaque size is fixed by the module grid, not by the art.
  const src = brand?.logoUrl || markUrl();
  return `<span class="qrmark" style="width:${mm}mm;height:${mm}mm">` +
    `<img src="${escapeHtml(src)}" alt="" style="width:${inner}mm;height:${inner}mm;object-fit:contain" />` +
    `</span>`;
}

/** Paired CSS. The element holding the code becomes the positioning context. */
export function qrCentreMarkCss(scope = ""): string {
  return `
    ${scope} .qrart, ${scope} .qrin { position: relative; display: inline-block; }
    ${scope} .qrmark {
      position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
      background: #FFFFFF; border-radius: 0.8mm;
      display: flex; align-items: center; justify-content: center;
    }
  `;
}

export async function qrForSize(
  url: string,
  sizeMm: number,
  {
    minModuleMm = MIN_MODULE_MM,
    label = "qr",
    ink,
  }: { minModuleMm?: number; label?: string; ink?: string } = {},
): Promise<PackQr> {
  let best: PackQr | null = null;
  for (const ecc of ["H", "Q", "M"] as const) {
    const q = await qrSvg(url, ecc, ink);
    const moduleMm = sizeMm / q.modules;
    const candidate: PackQr = { ...q, ecc, moduleMm };
    if (!best) best = candidate;
    if (moduleMm >= minModuleMm) return candidate;
  }
  throw new QrFloorError(
    `${label}: the code needs ${best!.moduleMm.toFixed(2)}mm modules at ${sizeMm}mm even at the ` +
      `lowest error correction, below the ${minModuleMm}mm floor for reliable phone scanning. ` +
      `Shorten the destination (currently ${url.length} characters) or print this piece larger.`,
    label,
    sizeMm,
  );
}
